import { tw } from "@careinfo/tailwind";
import { useState } from "react";
import {
  Controller,
  FieldPath,
  FieldValues,
  type Control,
} from "react-hook-form";
import makeAnimated from "react-select/animated";
import AsyncSelect from "react-select/async";
import useGetHook from "../../hooks/useGetHook";

const animatedComponents = makeAnimated();

interface Props<T extends FieldValues> {
  label: string;
  queryKey: string;
  name: FieldPath<T>;
  control: Control<T, any>;
  url: string;
  params?: Record<string, any>;
  limit?: number;
  dataLabel?: string;
  [key: string]: any;
}

export default function MultiSelectField<T extends FieldValues>({
  label,
  queryKey,
  params,
  name,
  control,
  url,
  dataLabel = "name",
}: Props<T>) {
  const [query, setQuery] = useState("");

  const { data: serverData } = useGetHook<any>({
    queryKey: [queryKey, `${queryKey}-${query}`],
    url: url,
    params: {
      search: query,
      ...params,
    },
  });

  const loadOptions = (
    inputValue: string,
    callback: (options: any) => void
  ) => {
    setQuery(inputValue?.length == 1 ? "" : inputValue);
    setTimeout(() => {
      callback(
        serverData?.map((item: any) => ({
          label: item?.[dataLabel] ?? item?.title,
          value: item?.id,
        }))
      );
    }, 1000);
  };

  return (
    <div>
      <label htmlFor="">{label}</label>
      <Controller
        control={control}
        name={name}
        defaultValue={null}
        render={({ field, fieldState: { error } }) => (
          <>
            <AsyncSelect
              {...field}
              onChange={(value) => {
                field.onChange(value);
                if (Array.isArray(value) && value.length === 0) {
                  setQuery("");
                }
              }}
              loadOptions={loadOptions}
              components={animatedComponents}
              closeMenuOnSelect={false}
              isMulti
              styles={{
                control(base, props) {
                  return {
                    ...base,
                    backgroundColor: "#EEF5F4",
                    border:
                      props.menuIsOpen || props.isFocused ? "none" : "none",
                    outline:
                      props.menuIsOpen || props.isFocused
                        ? "none !important"
                        : "none  !important",
                    cursor: "text",
                  };
                },
              }}
              defaultOptions={
                serverData?.map((item: any) => ({
                  label: item?.[dataLabel] ?? item?.title,
                  value: item?.id,
                })) ?? []
              }
            />
            {error && (
              <p className={tw`text-red-500 text-xs py-1`}>
                {JSON.stringify(error?.message)}
              </p>
            )}
          </>
        )}
      />
    </div>
  );
}
